import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import BugReportModal from './BugReportModal';
import './BugReports.css';
import HomeButton from './HomeButton';

const CHAT_API_BASE = '/api/chat';

const authFetch = async (url, options = {}) => {
  const token = await api.ensureAuthenticated();
  return fetch(url, { ...options, headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` } });
};

const STATUSES = ['Open', 'In Progress', 'Info Needed', 'Resolved', 'Closed', "Won't Fix"];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

const slug = (s) => (s || '').toLowerCase().replace(/[^a-z]+/g, '-');
// MySQL DATETIME values reach us as bare "YYYY-MM-DD HH:MM:SS" (no timezone
// suffix) but the DB stores them in UTC (server timezone is UTC). Parsing
// without a Z would make JS treat them as local, silently shifting hours.
// Append Z so toLocale* renders in the viewer's actual local timezone.
const parseUtc = (d) => d ? new Date(d.replace(' ', 'T') + 'Z') : null;
const fmtDate = (d) => { const dt = parseUtc(d); return dt ? dt.toLocaleDateString() : ''; };
// Full timestamp — used for response entries where "when exactly" matters
// (multiple responses in a day, audit trail).
const fmtDateTime = (d) => {
  const dt = parseUtc(d);
  if (!dt) return '';
  return dt.toLocaleDateString() + ' ' + dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

// Bug / feature tracker. Everyone can view and track status; developers
// (admins, surfaced via can_manage) can change status, assignee and resolution.
const BugTrackerPage = ({ onBack, currentUser }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canManage, setCanManage] = useState(false);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ type: '', status: '', mine: false, archived: false });
  const [expandedId, setExpandedId] = useState(null);
  const [edits, setEdits] = useState({});          // id -> {status, priority, assigned_user_id}
  const [savingId, setSavingId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [respDraft, setRespDraft] = useState({});   // id -> "in-progress reply text"
  const [postingResp, setPostingResp] = useState(null);
  const [attachments, setAttachments] = useState({}); // id -> [{id,name,file_url,mime}]

  const uid = currentUser?.id || '';

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.type) qs.set('type', filters.type);
      if (filters.status) qs.set('status', filters.status);
      if (filters.mine) qs.set('mine', '1');
      if (filters.archived) qs.set('archived', '1');
      if (uid) qs.set('user_id', uid);
      const res = await authFetch(`${CHAT_API_BASE}/bug-reports.php?${qs.toString()}`);
      const data = await res.json();
      if (data.success) {
        setReports(data.data || []);
        setCanManage(!!data.can_manage);
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [filters, uid]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.chatRequest('GET', `${CHAT_API_BASE}/users.php`);
        if (data.success && data.users) setUsers(data.users);
      } catch (e) { /* assignee dropdown simply unavailable */ }
    })();
  }, []);

  const toggleExpand = (r) => {
    if (expandedId === r.id) { setExpandedId(null); return; }
    setExpandedId(r.id);
    loadAttachments(r.id);
    setEdits(prev => ({
      ...prev,
      [r.id]: {
        status: r.status || 'Open',
        priority: r.priority || 'Medium',
        assigned_user_id: r.assigned_user_id || '',
      },
    }));
  };

  const postResponse = async (r) => {
    const content = (respDraft[r.id] || '').trim();
    if (!content) return;
    setPostingResp(r.id);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/bug-report-responses.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bug_report_id: r.id, content, user_id: uid }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.data) {
        // Append the new response to the row's local `responses` list without a full refetch
        setReports(prev => prev.map(x => x.id === r.id
          ? { ...x, responses: [...(x.responses || []), data.data] }
          : x));
        setRespDraft(prev => ({ ...prev, [r.id]: '' }));
      } else {
        alert(data.message || 'Failed to post response');
      }
    } catch (e) { alert('Failed to post response: ' + e.message); }
    setPostingResp(null);
  };

  // ── Attachments ────────────────────────────────────────────────
  const loadAttachments = async (id) => {
    try {
      const res = await authFetch(`${CHAT_API_BASE}/bug-attachments.php?bug_report_id=${encodeURIComponent(id)}`);
      const data = await res.json().catch(() => ({}));
      if (data.success) setAttachments(prev => ({ ...prev, [id]: data.data || [] }));
    } catch (e) { /* ignore */ }
  };
  const uploadAttachment = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('bug_report_id', id);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/bug-attachments.php`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) loadAttachments(id);
      else alert(data.error || 'Failed to attach file');
    } catch (e) { alert('Failed to attach file: ' + e.message); }
  };
  const deleteAttachment = async (id, attId) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      await authFetch(`${CHAT_API_BASE}/bug-attachments.php?id=${encodeURIComponent(attId)}`, { method: 'DELETE' });
      loadAttachments(id);
    } catch (e) { alert('Failed to remove: ' + e.message); }
  };

  const setEdit = (id, patch) => setEdits(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const saveReport = async (id, patch) => {
    setSavingId(id);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/bug-reports.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, user_id: uid, ...patch }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.data) {
        setReports(prev => prev.map(r => r.id === id ? data.data : r));
      } else {
        alert(data.message || 'Failed to update report');
      }
    } catch (e) {
      alert('Failed to update report: ' + e.message);
    }
    setSavingId(null);
  };

  // Quick inline status change from the row (developers only).
  const quickStatus = (r, status) => {
    setReports(prev => prev.map(x => x.id === r.id ? { ...x, status } : x));
    saveReport(r.id, { status });
  };

  // Archive: hides from the active list but keeps the row. Recovery lives in
  // the "Show archived" filter — flip the toggle to see hidden reports and
  // Unarchive to bring one back.
  const archiveReport = async (r) => {
    if (!window.confirm('Archive this report? It will be hidden from the active list but retained in the database and recoverable from the "Show archived" filter.')) return;
    try {
      const res = await authFetch(`${CHAT_API_BASE}/bug-reports.php?id=${encodeURIComponent(r.id)}&user_id=${encodeURIComponent(uid)}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) setReports(prev => prev.filter(x => x.id !== r.id));
      else alert(data.message || 'Failed to archive report');
    } catch (e) { alert('Failed to archive: ' + e.message); }
  };

  const unarchiveReport = async (r) => {
    try {
      const res = await authFetch(`${CHAT_API_BASE}/bug-reports.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: r.id, user_id: uid, archived: false }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        // Row is no longer archived — drop it from this list (we're in archived view)
        setReports(prev => prev.filter(x => x.id !== r.id));
      } else {
        alert(data.message || 'Failed to unarchive report');
      }
    } catch (e) { alert('Failed to unarchive: ' + e.message); }
  };

  const counts = STATUSES.reduce((acc, s) => { acc[s] = reports.filter(r => r.status === s).length; return acc; }, {});
  const openCount = (counts['Open'] || 0) + (counts['In Progress'] || 0) + (counts['Info Needed'] || 0);

  return (
    <div className="module-page bug-tracker-page">
      <div className="bug-tracker-header">
        <HomeButton onClick={onBack} />
        <button className="back-button" onClick={onBack}>← Back</button>
        <h1>Bug Tracker</h1>
        <div className="bug-tracker-headmeta">
          <span className="bug-open-count">
            {filters.archived ? `${reports.length} archived` : `${openCount} open`}
          </span>
          <button className="bug-btn-primary" onClick={() => setShowNew(true)}>+ New report</button>
        </div>
      </div>

      <div className="bug-tracker-filters">
        <div className="bug-filter-group">
          {['', 'bug', 'feature'].map(t => (
            <button
              key={t || 'all'}
              className={`bug-chip ${filters.type === t ? 'active' : ''}`}
              onClick={() => setFilters(f => ({ ...f, type: t }))}
            >{t === '' ? 'All' : t === 'bug' ? '🐞 Bugs' : '💡 Features'}</button>
          ))}
        </div>
        <select className="bug-select" value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="bug-mine-toggle">
          <input type="checkbox" checked={filters.mine} onChange={(e) => setFilters(f => ({ ...f, mine: e.target.checked }))} />
          My reports
        </label>
        <label className="bug-mine-toggle">
          <input type="checkbox" checked={filters.archived} onChange={(e) => { setExpandedId(null); setFilters(f => ({ ...f, archived: e.target.checked })); }} />
          Show archived
        </label>
      </div>

      {loading ? (
        <div className="bug-empty">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="bug-empty">No reports yet. Click <strong>+ New report</strong> to file one.</div>
      ) : (
        <div className="bug-list">
          {reports.map(r => {
            const expanded = expandedId === r.id;
            const e = edits[r.id] || {};
            return (
              <div key={r.id} className={`bug-card ${expanded ? 'expanded' : ''}`}>
                <div className="bug-card-row" onClick={() => toggleExpand(r)}>
                  <span className="bug-type-icon" title={r.type === 'feature' ? 'Feature request' : 'Bug'}>
                    {r.type === 'feature' ? '💡' : '🐞'}
                  </span>
                  <span className="bug-card-title">{r.title}</span>
                  <span className={`bug-badge priority-${slug(r.priority)}`}>{r.priority}</span>
                  <span className={`bug-badge status-${slug(r.status)}`}>{r.status}</span>
                  <span className="bug-card-meta">
                    {r.reporter_name || 'Someone'} · {fmtDate(r.date_entered)}
                  </span>
                  <span className="bug-card-caret">{expanded ? '▾' : '▸'}</span>
                </div>

                {expanded && (
                  <div className="bug-card-detail">
                    <div className="bug-detail-desc">
                      {r.description ? r.description : <em>No details provided.</em>}
                    </div>
                    <div className="bug-detail-meta">
                      <span><strong>Page:</strong> {r.page_url || '—'}</span>
                      {r.assigned_user_name && r.assigned_user_name.trim() && (
                        <span><strong>Assigned:</strong> {r.assigned_user_name}</span>
                      )}
                      <span><strong>Updated:</strong> {fmtDate(r.date_modified)}</span>
                    </div>

                    {r.screenshot_url && (
                      <div className="bug-detail-shot">
                        <a href={r.screenshot_url} target="_blank" rel="noopener noreferrer" title="Open full image in a new tab">
                          <img src={r.screenshot_url} alt="Report screenshot" />
                        </a>
                        <a className="bug-shot-open" href={r.screenshot_url} target="_blank" rel="noopener noreferrer">Open image ↗</a>
                      </div>
                    )}

                    <div className="bug-attachments">
                      <div className="bug-attachments-head">Attachments</div>
                      {(attachments[r.id] || []).length > 0 ? (
                        <ul className="bug-attachments-list">
                          {(attachments[r.id] || []).map(a => (
                            <li key={a.id}>
                              <a href={a.file_url} target="_blank" rel="noopener noreferrer" title={a.name}>📎 {a.name || 'file'}</a>
                              {canManage && (
                                <button className="bug-attach-remove" onClick={() => deleteAttachment(r.id, a.id)} aria-label="Remove">×</button>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="bug-attachments-empty"><em>No attachments.</em></div>
                      )}
                      <label className="bug-btn-secondary bug-attach-add">
                        📎 Attach file
                        <input type="file" style={{ display: 'none' }}
                          onChange={(ev) => { const f = ev.target.files?.[0]; ev.target.value = ''; uploadAttachment(r.id, f); }} />
                      </label>
                    </div>

                    {canManage && (
                      <div className="bug-manage">
                        <div className="bug-manage-grid">
                          <label>Status
                            <select value={e.status} onChange={(ev) => setEdit(r.id, { status: ev.target.value })}>
                              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </label>
                          <label>Priority
                            <select value={e.priority} onChange={(ev) => setEdit(r.id, { priority: ev.target.value })}>
                              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </label>
                          <label>Assignee
                            <select value={e.assigned_user_id} onChange={(ev) => setEdit(r.id, { assigned_user_id: ev.target.value })}>
                              <option value="">— Unassigned —</option>
                              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                          </label>
                        </div>
                        <div className="bug-manage-actions">
                          {filters.archived ? (
                            <button className="bug-btn-primary" onClick={() => unarchiveReport(r)}>Unarchive</button>
                          ) : (
                            <button className="bug-btn-danger" onClick={() => archiveReport(r)}>Archive</button>
                          )}
                          <span style={{ flex: 1 }} />
                          <button
                            className="bug-btn-primary"
                            disabled={savingId === r.id}
                            onClick={() => saveReport(r.id, {
                              status: e.status, priority: e.priority,
                              assigned_user_id: e.assigned_user_id,
                            })}
                          >{savingId === r.id ? 'Saving…' : 'Save'}</button>
                        </div>
                      </div>
                    )}

                    {/* Developer responses — immutable append-only log. Legacy
                        `resolution` text (single-field editable) is displayed
                        first as a read-only snapshot when present; new content
                        goes into jcrm_bug_report_responses via the input. */}
                    <div className="bug-responses">
                      <div className="bug-responses-head">Developer responses</div>
                      {r.resolution && r.resolution.trim() && (
                        <div className="bug-response-item bug-response-legacy">
                          <div className="bug-response-meta">Legacy note</div>
                          <div className="bug-response-body">{r.resolution}</div>
                        </div>
                      )}
                      {(r.responses || []).map(resp => (
                        <div key={resp.id} className="bug-response-item">
                          <div className="bug-response-meta">
                            <strong>{resp.user_name || 'Developer'}</strong>
                            <span className="bug-response-time">{fmtDateTime(resp.date_entered)}</span>
                          </div>
                          <div className="bug-response-body">{resp.content}</div>
                        </div>
                      ))}
                      {!(r.resolution && r.resolution.trim()) && (r.responses || []).length === 0 && (
                        <p className="bug-response-empty">No responses yet.</p>
                      )}
                      {canManage && (
                        <div className="bug-response-add">
                          <textarea
                            rows={2}
                            placeholder="Add a response…  (once posted, cannot be edited or deleted)"
                            value={respDraft[r.id] || ''}
                            onChange={(ev) => setRespDraft(prev => ({ ...prev, [r.id]: ev.target.value }))}
                          />
                          <button
                            className="bug-btn-primary"
                            disabled={postingResp === r.id || !(respDraft[r.id] || '').trim()}
                            onClick={() => postResponse(r)}
                          >{postingResp === r.id ? 'Posting…' : 'Post response'}</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {canManage && !expanded && (
                  <div className="bug-quickstatus" onClick={(ev) => ev.stopPropagation()}>
                    <select value={r.status} onChange={(ev) => quickStatus(r, ev.target.value)}>
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showNew && (
        <BugReportModal
          user={currentUser}
          currentPage="Bug Tracker"
          onClose={() => setShowNew(false)}
          onSubmitted={() => { setShowNew(false); fetchReports(); }}
        />
      )}
    </div>
  );
};

export default BugTrackerPage;
